import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import {
  analyzeURL,
  getExampleURL,
  getSuspiciousExampleURL,
  type URLAnalysis,
  type SecurityIssue,
} from '@utils/urlSafetyChecker';
import { useClipboard } from '@hooks/useClipboard';
import { InvisibleCharacterAnalysis } from './InvisibleCharacterAnalysis';
import { AnalysisCache } from '@utils/debounce';

// Global cache for URL analysis results
const urlAnalysisCache = new AnalysisCache();

export const UrlSafetyTab = () => {
  const [inputUrl, setInputUrl] = useState('');
  const [analysis, setAnalysis] = useState<URLAnalysis | null>(null);
  const [error, setError] = useState('');
  const [highlightedPosition, setHighlightedPosition] = useState<number | null>(null);
  
  // Use clipboard hook for cross-browser support
  const { pasteFromClipboard, error: clipboardError, clearError } = useClipboard();

  const handleAnalyze = () => {
    setError('');
    setAnalysis(null);

    if (!inputUrl.trim()) {
      setError('Please enter a URL to analyze');
      return;
    }

    // Check cache first
    const cacheKey = AnalysisCache.textHash(inputUrl.trim());
    if (urlAnalysisCache.has(cacheKey)) {
      const cachedResult = urlAnalysisCache.get<URLAnalysis>(cacheKey);
      if (cachedResult) {
        setAnalysis(cachedResult);
        return;
      }
    }

    const result = analyzeURL(inputUrl);
    if (!result) {
      setError('Invalid URL format. Please check the URL and try again.');
      return;
    }

    // Cache the result
    urlAnalysisCache.set(cacheKey, result);
    setAnalysis(result);
  };

  const handlePasteFromClipboard = async () => {
    const text = await pasteFromClipboard();
    if (text) {
      setInputUrl(text);
      setError('');
      setAnalysis(null);
    }
  };

  const handleTryExample = () => {
    setInputUrl(getExampleURL());
    setError('');
    setAnalysis(null);
  };

  const handleTrySuspicious = () => {
    setInputUrl(getSuspiciousExampleURL());
    setError('');
    setAnalysis(null);
  };

  const handleClear = () => {
    setInputUrl('');
    setAnalysis(null);
    setError('');
    setHighlightedPosition(null);
  };

  const handlePositionClick = (position: number) => {
    setHighlightedPosition(position);
    // Clear highlight after 3 seconds
    setTimeout(() => {
      setHighlightedPosition(null);
    }, 3000);
  };

  const getRiskBadge = (riskLevel: URLAnalysis['riskLevel']) => {
    const colorMap: Record<URLAnalysis['riskLevel'], 'green' | 'grey' | 'red'> = {
      safe: 'green',
      suspicious: 'grey',
      dangerous: 'red',
    };

    return <Badge color={colorMap[riskLevel]}>{riskLevel.toUpperCase()}</Badge>;
  };

  const getSeverityBadge = (severity: SecurityIssue['severity']) => {
    const colorMap: Record<SecurityIssue['severity'], 'green' | 'grey' | 'red'> = {
      low: 'green',
      medium: 'grey',
      high: 'red',
    };

    return <Badge color={colorMap[severity]}>{severity.toUpperCase()}</Badge>;
  };

  return (
    <SpaceBetween size="l">
      {clipboardError && (
        <Alert key="clipboard-error-alert" type="warning" dismissible onDismiss={clearError}>
          {clipboardError}
        </Alert>
      )}

      <Container key="main-container"
        header={
          <Header
            variant="h2"
            description="Detect and analyze invisible characters hidden in URLs. Identifies zero-width characters, directional marks, and other hidden Unicode that can be used to disguise malicious links or bypass security filters."
          >
            Check URL on invisible Characters
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Textarea
            value={inputUrl}
            onChange={({ detail }) => setInputUrl(detail.value)}
            placeholder="Enter a URL to analyze (e.g., https://www.example.com/path)"
            rows={10}
          />

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button onClick={handlePasteFromClipboard}>Paste from Clipboard</Button>
            <Button onClick={handleAnalyze}>
              Analyze URL
            </Button>
            <Button onClick={handleTryExample}>Try Safe Example</Button>
            <Button onClick={handleTrySuspicious}>Try Suspicious Example</Button>
            <Button onClick={handleClear}>Clear</Button>
          </div>

          {error && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Container>

      {analysis && (
        <>
          {/* Risk Overview */}
          <Container
            header={
              <Header variant="h2" description={`Security score: ${analysis.score}/100`}>
                Risk Assessment
              </Header>
            }
          >
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Risk Level</Box>
                <div>{getRiskBadge(analysis.riskLevel)}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Issues Found</Box>
                <div>{analysis.issues.length} potential security concern{analysis.issues.length !== 1 ? 's' : ''}</div>
              </div>
            </ColumnLayout>
          </Container>

          {/* URL Breakdown */}
          <Container header={<Header variant="h2">URL Components</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="s">
                <div>
                  <Box variant="awsui-key-label">Protocol</Box>
                  <div style={{ fontFamily: 'monospace' }}>{analysis.components.protocol}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Hostname</Box>
                  <div style={{ fontFamily: 'monospace' }}>{analysis.components.hostname}</div>
                  {analysis.components.punycode && (
                    <div style={{ fontSize: '11px', color: '#5f6b7a', marginTop: '4px' }}>
                      Punycode: {analysis.components.punycode}
                    </div>
                  )}
                </div>
                {analysis.components.port && (
                  <div>
                    <Box variant="awsui-key-label">Port</Box>
                    <div style={{ fontFamily: 'monospace' }}>{analysis.components.port}</div>
                  </div>
                )}
                {(analysis.components.username || analysis.components.password) && (
                  <div>
                    <Box variant="awsui-key-label">Credentials</Box>
                    <Alert type="error">
                      URL contains embedded credentials (security risk)
                    </Alert>
                  </div>
                )}
              </SpaceBetween>
              <SpaceBetween size="s">
                {analysis.components.pathname !== '/' && (
                  <div>
                    <Box variant="awsui-key-label">Path</Box>
                    <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {analysis.components.pathname}
                    </div>
                  </div>
                )}
                {analysis.components.search && (
                  <div>
                    <Box variant="awsui-key-label">Query String</Box>
                    <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {analysis.components.search}
                    </div>
                  </div>
                )}
                {analysis.components.hash && (
                  <div>
                    <Box variant="awsui-key-label">Fragment</Box>
                    <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {analysis.components.hash}
                    </div>
                  </div>
                )}
              </SpaceBetween>
            </ColumnLayout>
          </Container>

          {/* Security Issues (excluding invisible characters, which are shown separately) */}
          {(() => {
            const nonInvisibleIssues = analysis.issues.filter(issue => issue.type !== 'Invisible Characters');
            
            if (nonInvisibleIssues.length > 0) {
              return (
                <Container
                  header={
                    <Header variant="h2" description="Potential security concerns detected in this URL">
                      Security Issues
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    {nonInvisibleIssues.map((issue, index) => (
                      <ExpandableSection key={index} headerText={issue.type} variant="container">
                        <SpaceBetween size="s">
                          <div>
                            <Box variant="awsui-key-label">Severity</Box>
                            <div>{getSeverityBadge(issue.severity)}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Description</Box>
                            <div>{issue.description}</div>
                          </div>
                        </SpaceBetween>
                      </ExpandableSection>
                    ))}
                  </SpaceBetween>
                </Container>
              );
            }
            
            if (analysis.issues.length === 0) {
              return (
                <Alert type="success">
                  No obvious security issues detected. The URL appears to be safe, but always verify the source and use caution when clicking links.
                </Alert>
              );
            }
            
            return null;
          })()}

          {/* Analysis Results - Invisible Characters */}
          {analysis.invisibleCharAnalysis && analysis.invisibleCharAnalysis.hasInvisibleChars && (
            <InvisibleCharacterAnalysis
              text={inputUrl}
              detectedChars={analysis.invisibleCharAnalysis.detectedChars}
              title="Analysis Results"
              description="Detailed analysis of invisible characters and security patterns"
              showLineNumbers={false}
              showSpacesControl={false}
              showUnicodeControl={true}
              showActionButtons={false}
              allowCopyCharacter={true}
              securityAnalysis={analysis.invisibleCharAnalysis.securityAnalysis || null}
              onCharacterClick={handlePositionClick}
              highlightedPosition={highlightedPosition}
            />
          )}
        </>
      )}
    </SpaceBetween>
  );
};

